import moment from "moment";
import { CandidateModel } from "../../_models/data.candidate.model";
import { QualificationModel } from "../../_models/data.qualification.model";
import { CandidateCvDataInterface, CandidateInterface } from "../../_types/candidate";
import { QualificationInterface } from "../../_types/qualification";
import { EmploymentModel } from "../../_models/data.employment.model";
import { EmploymentInterface } from "../../_types/employment";
import { ReferenceModel } from "../../_models/data.reference.model";
import { ReferenceInterface } from "../../_types/reference";
import { CandidateStatus } from "../../_enums/candidateStatus.enum";
import { NextOfKinModel } from "../../_models/data.nextOfKin.model";
import { CriminalRecordModel } from "../../_models/data.criminalRecord.model";
import { SafeInEducationModel } from "../../_models/data.safeInEducation.model";
import { HealthRecordModel } from "../../_models/data.healthRecord.model";
import { BankDetailsModel } from "../../_models/data.bankDetails.model";
import { SocialMediaModel } from "../../_models/data.socialMedia.model";
import { CandidatePay } from "../../_enums/candidatePay.enum";
import { FormQuestionModuleType, FormQuestionType, IFormQuestion } from "../../state/context/formQuestions.store";
import { teachingOccupations } from "../../components/forms/candidate/contactInformation";
import { ShowFormQuestion } from "../helpers";
import { OccupationTypes } from "../../_enums/occipationTypes.enum";

export const formattedCandidateData = (candidate: CandidateCvDataInterface, status?: CandidateStatus) => {
	return new CandidateModel({
		forename: candidate.forename,
		surname: candidate.surname,
		email: candidate.email,
		telephone: candidate.telephone,
		mobile: candidate.telephone,
		address: {
			addressLine1: candidate.address?.addressLine1 || "",
			addressLine2: "",
			addressLine3: "",
			city: candidate.address?.city || "",
			county: candidate.address?.county || "",
			postcode: candidate.address?.postcode || "",
		},
		qualifications: candidate.qualifications?.map(
			(q) =>
				new QualificationModel({
					place: q.place,
					qualificationName: q.type,
					date: moment(q.studiedTo_ISO08601).isValid() ? moment(q.studiedTo_ISO08601).format("DD/MM/YYYY") : q.studiedTo_ISO08601,
				})
		) as QualificationInterface[],
		employment: candidate.employment?.map(
			(w) =>
				new EmploymentModel({
					employerName: w.company,
					dateStarted: moment(w.workedFrom_ISO08601).isValid() ? moment(w.workedFrom_ISO08601).format("DD/MM/YYYY") : w.workedFrom_ISO08601,
					dateEnded: moment(w.workedTo_ISO08601).isValid() ? moment(w.workedTo_ISO08601).format("DD/MM/YYYY") : w.workedTo_ISO08601,
					jobTitle: w.position,
				})
		) as EmploymentInterface[],
		references: candidate.references?.map(
			(r) =>
				new ReferenceModel({
					referenceForename: r.referenceForename,
					referenceSurname: r.referenceSurname,
					referenceCompany: r.referenceCompany,
					referencePosition: r.referencePosition,
					referenceEmail: r.referenceEmail,
					referenceTelephone: r.referenceTelephone,
				})
		) as ReferenceInterface[],
		dob: moment(candidate.dob).isValid() ? moment(candidate.dob).format("DD/MM/YYYY") : "",
		status: status || CandidateStatus.Pending,
		nextOfKin: new NextOfKinModel(),
		criminalRecord: new CriminalRecordModel(),
		safeInEducation: new SafeInEducationModel(),
		healthRecord: new HealthRecordModel(),
		bankDetails: new BankDetailsModel(),
		socialMedia: new SocialMediaModel(),
		referenceCheckConsent: false,
		referenceCheckConsentName: "",
		referenceCheckConsentDate: "",
		safeInEducationCheckConsent: false,
		safeInEducationCheckConsentName: "",
		safeInEducationCheckConsentDate: "",
		healthRecordCheckConsent: false,
		healthRecordCheckConsentName: "",
		healthRecordCheckConsentDate: "",
		criminalRecordCheckConsent: false,
		criminalRecordCheckConsentName: "",
		criminalRecordCheckConsentDate: "",
		declarationCheckConsent: false,
		declarationCheckConsentName: "",
		declarationCheckConsentDate: "",
		confirmed: false,
		contractForServiceConsent: false,
		contractForServiceName: "",
		contractForServiceDate: "",
		profilePhoto: "",
		payType: CandidatePay.Paye,
		previousAgencies: [],
		previousAddresses: [],
	});
};

export const getDifferences = (currentData, newData) => {
	const fieldsToCheck = {
		forename: null,
		surname: null,
		email: null,
		mobile: null,
		dob: null,
		addressLine1: null,
		addressLine2: null,
		addressLine3: null,
		city: null,
		county: null,
		postcode: null,
	};
	if (currentData.forename && newData.forename && currentData.forename !== newData.forename) {
		fieldsToCheck.forename = [currentData.forename, newData.forename];
	}
	if (currentData.surname && newData.surname && currentData.surname !== newData.surname) {
		fieldsToCheck.surname = [currentData.surname, newData.surname];
	}
	if (currentData.email && newData.email && currentData.email !== newData.email) {
		fieldsToCheck.email = [currentData.email, newData.email];
	}
	if (currentData.mobile && newData.mobile && currentData.mobile !== newData.mobile) {
		fieldsToCheck.mobile = [currentData.mobile, newData.mobile];
	}
	if (currentData.dob && newData.dob && currentData.dob !== newData.dob) {
		fieldsToCheck.dob = [currentData.dob, newData.dob];
	}
	if (currentData.address?.addressLine1 && newData.address?.addressLine1 && currentData.address?.addressLine1 !== newData.address?.addressLine1) {
		fieldsToCheck.addressLine1 = [currentData.address?.addressLine1, newData.address?.addressLine1];
	}
	if (currentData.address?.addressLine2 && newData.address?.addressLine2 && currentData.address?.addressLine2 !== newData.address?.addressLine2) {
		fieldsToCheck.addressLine2 = [currentData.address?.addressLine2, newData.address?.addressLine2];
	}
	if (currentData.address?.addressLine3 && newData.address?.addressLine3 && currentData.address?.addressLine3 !== newData.address?.addressLine3) {
		fieldsToCheck.addressLine3 = [currentData.address?.addressLine3, newData.address?.addressLine3];
	}
	if (currentData.address?.city && newData.address?.city && currentData.address?.city !== newData.address?.city) {
		fieldsToCheck.city = [currentData.address?.city, newData.address?.city];
	}
	if (currentData.address?.county && newData.address?.county && currentData.address?.county !== newData.address?.county) {
		fieldsToCheck.county = [currentData.address?.county, newData.address?.county];
	}
	if (currentData.address?.postcode && newData.address?.postcode && currentData.address?.postcode !== newData.address?.postcode) {
		fieldsToCheck.postcode = [currentData.address?.postcode, newData.address?.postcode];
	}
	if (
		!fieldsToCheck.forename &&
		!fieldsToCheck.surname &&
		!fieldsToCheck.email &&
		!fieldsToCheck.mobile &&
		!fieldsToCheck.dob &&
		!fieldsToCheck.addressLine1 &&
		!fieldsToCheck.addressLine2 &&
		!fieldsToCheck.addressLine3 &&
		!fieldsToCheck.city &&
		!fieldsToCheck.county &&
		!fieldsToCheck.postcode
	) {
		return null;
	} else {
		return fieldsToCheck;
	}
};

export const arraysToMerge = (currentState, newState) => {
	const formattedNewState = {
		qualifications: newState.qualifications.map((q) => {
			return new QualificationModel({
				place: q.place,
				qualificationName: q.type,
				date: moment(q.studiedTo_ISO08601).isValid() ? moment(q.studiedTo_ISO08601).format("DD/MM/YYYY") : q.studiedTo_ISO08601,
			});
		}) as QualificationInterface[],
		employment: newState.employment.map(
			(w) =>
				new EmploymentModel({
					employerName: w.company,
					dateStarted: moment(w.workedFrom_ISO08601).isValid() ? moment(w.workedFrom_ISO08601).format("DD/MM/YYYY") : w.workedFrom_ISO08601,
					dateEnded: moment(w.workedTo_ISO08601).isValid() ? moment(w.workedTo_ISO08601).format("DD/MM/YYYY") : w.workedTo_ISO08601,
					jobTitle: w.position,
				})
		) as EmploymentInterface[],
		references: newState.references?.map(
			(r) =>
				new ReferenceModel({
					referenceForename: r.referenceForename,
					referenceSurname: r.referenceSurname,
					referenceCompany: r.referenceCompany,
					referencePosition: r.referencePosition,
					referenceEmail: r.referenceEmail,
					referenceTelephone: r.referenceTelephone,
				})
		) as ReferenceInterface[],
	};

	return {
		references: [...currentState.references, ...formattedNewState.references],
		qualifications: [...currentState.qualifications, ...formattedNewState.qualifications],
		employment: [...currentState.employment, ...formattedNewState.employment],
	};
};

export const MergeJustArrays = (currentState, mergedArrays, populatedFromCv, documentId) => {
	const updatedState = { ...currentState };
	updatedState.references = mergedArrays.references;
	updatedState.employment = mergedArrays.employment;
	updatedState.qualifications = mergedArrays.qualifications;

	populatedFromCv(updatedState, documentId, true);
};

export const MergeParsedData = (data, currentState, populatedFromCv, documentId) => {
	const updatedState = { ...currentState };
	updatedState.references = [...currentState.references, ...data.fieldsToMerge.references];
	updatedState.employment = [...currentState.employment, ...data.fieldsToMerge.employment];
	updatedState.qualifications = [...currentState.qualifications, ...data.fieldsToMerge.qualifications];
	for (let field of Object.keys(data.fieldsToUpdate)) {
		if (field === "addressLine1" || field === "addressLine2" || field === "addressLine3" || field === "city" || field === "county" || field === "postcode") {
			updatedState.address[field] = data.fieldsToUpdate[field];
		} else {
			updatedState[field] = data.fieldsToUpdate[field];
		}
	}
	for (let field of Object.keys(data.newFields)) {
		updatedState[field] = data.newFields[field];
	}
	populatedFromCv(updatedState, documentId, true);
};

export const checkNonExistingFields = (currentData, newData) => {
	let newFields: Partial<CandidateInterface> = {};
	if (currentData.address) {
		newFields.address = currentData.address;
	} else {
		newFields.address = {};
	}
	if (newData.forename && !currentData.forename) {
		newFields.forename = newData.forename;
	}
	if (newData.surname && !currentData.surname) {
		newFields.surname = newData.surname;
	}
	if (newData.email && !currentData.email) {
		newFields.email = newData.email;
	}
	if (newData.mobile && !currentData.mobile) {
		newFields.mobile = newData.mobile;
	}
	if (newData.dob && !currentData.dob) {
		newFields.dob = newData.dob;
	}

	if (newData.address?.addressLine1 && !currentData.address?.addressLine1) {
		newFields.address.addressLine1 = newData.address?.addressLine1;
	}
	if (newData.address?.addressLine2 && !currentData.address?.addressLine2) {
		newFields.address.addressLine2 = newData.address?.addressLine2;
	}
	if (newData.address?.addressLine3 && !currentData.address?.addressLine3) {
		newFields.address.addressLine3 = newData.address?.addressLine3;
	}
	if (newData.address?.city && !currentData.address?.city) {
		newFields.address.city = newData.address?.city;
	}
	if (newData.address?.county && !currentData.address?.county) {
		newFields.address.county = newData.address?.county;
	}
	if (newData.address?.postcode && !currentData.address?.postcode) {
		newFields.address.postcode = newData.address?.postcode;
	}
	return newFields;
};

export const SelectAllFields = (setState, oldState, value) => {
	const selectAllFields = { ...oldState };
	for (let field of Object.keys(oldState)) {
		selectAllFields[field] = value;
	}
	setState(selectAllFields);
};

export const capitalizeFirstLetter = (string) => {
	return string.charAt(0).toUpperCase() + string.slice(1);
};

export const checkAllFieldsAreSameType = (fields, type) => {
	let outcome = true;
	for (let field of Object.keys(fields)) {
		if (fields[field] !== type) {
			outcome = false;
		}
	}
	return outcome;
};

export const canSendOnboardingToCandidate = (status: CandidateStatus) => {
	if (!status) return false;
	return status == CandidateStatus.Pending || status == CandidateStatus.Approved || status == CandidateStatus.Hold || status == CandidateStatus.Future || status == CandidateStatus.Pre
		? true
		: false;
};

export const shouldShowTrnField = (occupation: OccupationTypes | undefined, formQuestions: IFormQuestion[]): boolean => {
	const isTeachingRelated = !!occupation && (teachingOccupations.has(occupation) || occupation.toLowerCase().includes("teach"));
	const shouldShowMedicalRequirements = ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.onboardingMedicalRequirements);
	const shouldAlwaysShowTrn = ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.showTrnNumberField);
	return isTeachingRelated || shouldShowMedicalRequirements || shouldAlwaysShowTrn;
};

export const sanitizeCandidateForComparison = (data: CandidateInterface) => {
	if (!data) return null;
	const model = new CandidateModel(data);
	return {
		forename: model.forename,
		surname: model.surname,
		previousNames: model.previousNames,
		otherNames: model.otherNames,
		email: model.email,
		mobile: model.mobile,
		telephone: model.telephone,
		occupation: model.occupation,
		trnNumber: model.trnNumber,
		trnNumberNotes: model.trnNumberNotes,
		employmentStartDate: model.employmentStartDate,
		ewcConfirmation: model.ewcConfirmation,
		homeOfficeShareCode: model.homeOfficeShareCode,
		subject: model.subject,
		dob: model.dob,
		sex: model.sex,
		optOut: model.optOut,
		optOut48Hours: model.optOut48Hours,
		transport: model.transport,
		importantNotes: model.importantNotes,
		source: model.source,
		sourceOther: model.sourceOther,
		title: model.title,
		pronoun: model.pronoun,
		teacherPayScale: model.teacherPayScale,
		p45: model.p45,
		roleType: model.roleType,
		desiredLocation: model.desiredLocation,
		ltdCompany: model.ltdCompany,
		desiredLocationMultiple: model.desiredLocationMultiple,
		subsidiarySubject: model.subsidiarySubject,
		employmentPreference: model.employmentPreference,
		address: model.address,
		nextOfKin: model.nextOfKin,
		bankDetails: model.bankDetails,
		criminalRecord: model.criminalRecord,
		safeInEducation: model.safeInEducation,
		socialMedia: model.socialMedia,
		healthRecord: model.healthRecord,
		qualifications: model.qualifications,
		employment: model.employment,
		previousAgencies: model.previousAgencies,
		previousAddresses: model.previousAddresses,
		references: (model.references || []).map((ref: any) => ({
			_id: ref._id,
			name: ref.name || "",
			email: ref.email || "",
			phone: ref.phone || "",
			relationship: ref.relationship || "",
			company: ref.company || "",
			position: ref.position || "",
			dateFrom: ref.dateFrom || "",
			dateTo: ref.dateTo || "",
			notes: ref.notes || "",
		})),
		referenceCheckConsent: model.referenceCheckConsent,
		referenceCheckConsentName: model.referenceCheckConsentName,
		safeInEducationCheckConsent: model.safeInEducationCheckConsent,
		safeInEducationCheckConsentName: model.safeInEducationCheckConsentName,
		healthRecordCheckConsent: model.healthRecordCheckConsent,
		healthRecordCheckConsentName: model.healthRecordCheckConsentName,
		criminalRecordCheckConsent: model.criminalRecordCheckConsent,
		criminalRecordCheckConsentName: model.criminalRecordCheckConsentName,
		declarationCheckConsent: model.declarationCheckConsent,
		declarationCheckConsentName: model.declarationCheckConsentName,
		contractForServiceConsent: model.contractForServiceConsent,
		contractForServiceName: model.contractForServiceName,
		referenceCheckOverride: model.referenceCheckOverride,
		declarationCheckOverride: model.declarationCheckOverride,
		safeInEducationOverride: model.safeInEducationOverride,
		criminalRecordOverride: model.criminalRecordOverride,
		contractForServiceOverride: model.contractForServiceOverride,
		healthDeclarationOverride: model.healthDeclarationOverride,
	};
};
